import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { appActions, appSelectors } from '../reducer/app-reducer';
import { withRouter } from 'react-router-dom';
import styled from '@emotion/styled';
import { appMainBackground, appTextColor, mainLoginBackground } from '../../../styles/colors';
import { footerHeight, headerHeight } from '../../../styles/constants';
import { ltrDir, rtlDir } from '../constants';
import { AppHeaderContainer as AppHeader } from './AppHeader';
import { AppFooterContainer as AppFooter } from './AppFooter';
import { NewAppFooterContainer as NewAppFooter } from './NewAppFooter';
import { ssoLoginSelectors } from '../../login/reducer/sso-login-reducer';
import { withWindowDimensions } from '@rosetta/react-common-ui';
import { isMobile as isMobileDetected } from 'react-device-detect';
import LoginBackgroundImage from '../../../assets/images/login-background-image.jpg';

// thanks, IE
import 'babel-polyfill';
import { lionSelectors } from '@rosetta/react-lion/dist/index';

let isRtl = false;

const AppDiv = styled.div((props) => {
  let style = {
    boxSizing: 'border-box',
    '& *': {
      boxSizing: 'border-box'
    },
    fontSize: '0px',
    color: appTextColor,
    backgroundColor: mainLoginBackground,
    cursor: 'default',
    userSelect: 'none',
    position: !props.isMobile ? 'absolute' : 'relative',
    top: '0px',
    right: '0px',
    bottom: '0px',
    left: '0px',
    height: '100%',
    '@media (max-height: 715px)': {
      height: props.isLoginScreen ? '110%' : '100%'
    }
  };

  if (props.isLoginScreen) {
    style.background = `linear-gradient(0deg, rgba(34, 39, 46, 0.6), rgba(34, 39, 46, 0.6)),
    linear-gradient(180deg, #000000 0%, rgba(0, 0, 0, 0) 45.92%),
    url(${LoginBackgroundImage}) no-repeat center center / cover`;
  }

  if (isRtl) {
    style['& *'].direction = rtlDir;
  } else {
    style['& *'].direction = ltrDir;
  }

  return style;
});

const AppContent = styled.div((props) => {
  let style = {
    position: props.isMobile ? 'relative' : props.isLoginScreen ? 'relative' : 'absolute',
    left: 0,
    right: 0
  };
  if (!props.isMobile) {
    if (props.hasHeader) {
      style.top = headerHeight;
    }
    if (props.hasFooter) {
      style.bottom = props.isLoginScreen ? 0 : footerHeight;
    }
  }

  return style;
});

const FooterWrapper = styled.div`
  box-shadow: 0px -8px 5px 0px #0000001a;
  width: 100%;
`;

export class App extends PureComponent {
  constructor(props) {
    super(props);
    const body = document.body;
    body.style.position = 'absolute';
    body.style.boxSizing = 'border-box';
    body.style.top = '0px';
    body.style.right = '0px';
    body.style.bottom = '0px';
    body.style.left = '0px';
    body.style.margin = '0px';
    body.style.padding = '0px';
    body.style.backgroundColor = appMainBackground;
    body.style.fontFamily =
      'NotoSans, NotoSans_Hebrew, NotoSans_Arabic, NotoSans_Hindi, NotoSans_JP, NotoSans_KR, NotoSans_TC, NotoSans_SC';

    this.state = {
      localizedToLanguage: '',
      unsupportedBrowser: false,
      unsupportedOriginUrl: ''
    };
    this.props.initApp();
  }

  static getDerivedStateFromProps = (nextProps, prevState) => {
    if (nextProps.lionLoaded && nextProps.lionLanguage !== prevState.localizedToLanguage) {
      const appTitle = nextProps.lionTranslateString('_html_page_title').replace(/&reg;/g, '\u00AE');
      document.title = appTitle;
      return {
        localizedToLanguage: nextProps.lionLanguage
      };
    }
    if (nextProps.unsupportedBrowser && prevState.unsupportedBrowser !== nextProps.unsupportedBrowser) {
      nextProps.setUnsupportedOriginUrl(nextProps.location.pathname);
      nextProps.history.push('/unsupported');
      return { unsupportedBrowser: nextProps.unsupportedBrowser, unsupportedOriginUrl: nextProps.location.pathname };
    }
    // logoutUser in regiser-sagas redirects user to /login page, this will make sure we still show them /unsupported
    if (nextProps.location.pathname === '/login' && prevState.unsupportedOriginUrl.includes('register')) {
      nextProps.history.push('/unsupported');
      return null;
    }
    return null;
  };

  render = () => {
    // We shouldn't be controlling UI like this - will become hard to maintain
    // Perhaps consider rewriting the router https://reactrouter.com/en/main/routers/router-provider
    // should emcompass `login`, `forgot-password`, `webssologin`, `reset_password/213`
    // This also has to be determined on render instead of state because SPA.
    const isLoginScreen =
      ['/login', '/forgot-password', '/webssologin'].includes(this.props.location.pathname) ||
      /reset_password/i.test(this.props.location.pathname);

    isRtl = this.props.isInterfaceRtl;
    const isMobile = isMobileDetected ? true : false;

    let header = null;
    let footer = null;
    if (this.props.location.pathname.indexOf('objectives') >= 0) {
      header = <AppHeader />;
    } else if (
      this.props.location.pathname.indexOf('mobilessologin') === -1 &&
      this.props.location.pathname.indexOf('ssoerror') === -1 &&
      !this.props.ssoLoginAttemptPending
    ) {
      header = <AppHeader />;
      footer = <AppFooter />;
    }

    // Hide the header for the new login page
    if (isLoginScreen) {
      header = null;
      footer = <NewAppFooter />;
    }

    return (
      <AppDiv data-qa="AppDiv" isMobile={isMobile} isLoginScreen={isLoginScreen}>
        {header}
        <AppContent
          data-qa="AppContent"
          hasHeader={header !== null}
          hasFooter={footer !== null}
          isMobile={isMobile}
          windowHeight={this.props.clientHeight}
          isLoginScreen={isLoginScreen}
        >
          {this.props.children}
        </AppContent>
        <FooterWrapper>{footer}</FooterWrapper>
      </AppDiv>
    );
  };
}

App.propTypes = {};

const mapStateToProps = (state, ownprops) => ({
  isInterfaceRtl: appSelectors.isInterfaceRtl(state),
  unsupportedBrowser: appSelectors.getUnsupportedBrowser(state),
  ssoLoginAttemptPending: ssoLoginSelectors.ssoLoginAttemptPending(state),
  lionLanguage: lionSelectors.getLionLanguage(state),
  lionLoaded: lionSelectors.getLionAppResourcesLoaded(state),
  lionTranslateString: (key, placeholders, values) => {
    return lionSelectors.getLionTranslatedString(state, key, placeholders, values);
  }
});

export const AppContainer = withRouter(
  connect(
    mapStateToProps,
    appActions
  )(withWindowDimensions(App))
);



// WEBPACK FOOTER //
// ./src/modules/app/components/App.jsx